"use client";

import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  type Chain,
  type HttpTransport,
  type PrivateKeyAccount,
  type WalletClient,
} from "viem";
import { parseUnits } from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import {
  EIP712Domain,
  Pair,
  withSignatureErrorHandler,
} from "#/utils/signature";

import { transfer } from "../kyan.service";
import {
  type SignedUserTransfer,
  type TransferRequest,
  type TransferResponse,
} from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";

const UserTransfer = [
  { name: "deadline", type: "uint256" },
  { name: "account", type: "address" },
  { name: "fromPair", type: "Pair" },
  { name: "toPair", type: "Pair" },
  { name: "amount", type: "uint256" },
];

const signTransfer = async (
  transferRequest: Omit<TransferRequest, "signature" | "signature_deadline">,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<SignedUserTransfer> => {
  const signature_deadline = getSignatureDeadline();

  return {
    ...transferRequest,
    signature_deadline,
    signature: await walletClient.signTypedData({
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { UserTransfer, Pair },
      primaryType: "UserTransfer",
      message: {
        account: transferRequest.account,
        fromPair: transferRequest.from_pair,
        toPair: transferRequest.to_pair,
        amount: parseUnits(transferRequest.amount.toString(), 6),
        deadline: signature_deadline,
      },
    }),
  };
};

const validateTransferSignature = async (
  payload: SignedUserTransfer,
  chainId: ChainId,
): Promise<boolean> => {
  const publicClient = getPublicClient(chainId);

  return await publicClient.verifyTypedData({
    address: payload.account,
    domain: EIP712Domain[chainId],
    types: { UserTransfer, Pair },
    primaryType: "UserTransfer",
    message: {
      deadline: payload.signature_deadline,
      account: payload.account,
      fromPair: payload.from_pair,
      toPair: payload.to_pair,
      amount: parseUnits(payload.amount.toString(), 6),
    },
    signature: payload.signature,
  });
};

export const useTransfer = (
  options?: UseMutationOptions<
    TransferResponse,
    Error,
    Pick<TransferRequest, "account" | "from_pair" | "to_pair" | "amount">
  > & {
    onPendingTransfer?: (signedTransfer: SignedUserTransfer) => void;
  },
) => {
  const { walletClient } = useWalletClient();

  return useMutation({
    ...options,
    mutationFn: async ({ account, from_pair, to_pair, amount }) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const signedTransfer = await withSignatureErrorHandler(async () =>
        signTransfer(
          {
            account,
            from_pair,
            to_pair,
            amount,
          },
          walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
        ),
      );

      const isSignatureValid = await validateTransferSignature(
        signedTransfer,
        walletClient.chain?.id as ChainId,
      );

      options?.onPendingTransfer?.(signedTransfer);

      return defaultApiResponseHandler({
        serviceName: "Transfer",
        payload: signedTransfer,
        service: transfer,
        validation: [isSignatureValid],
      });
    },
  });
};
